/**
 * PopupNotification Component
 * Displays popup notifications to users after login with rich content support
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { X, Info, AlertTriangle, CheckCircle, Gift, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { API } from '../App';

const POPUP_ICONS = {
  info: { icon: Info, color: 'text-blue-500', bg: 'bg-blue-50', border: 'border-blue-200' },
  warning: { icon: AlertTriangle, color: 'text-amber-500', bg: 'bg-amber-50', border: 'border-amber-200' },
  success: { icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-50', border: 'border-green-200' },
  promotion: { icon: Gift, color: 'text-purple-500', bg: 'bg-purple-50', border: 'border-purple-200' }
};

const PopupNotification = ({ user }) => {
  const [popups, setPopups] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const fetchPopups = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/popups`);
      if (response.data && response.data.length > 0) {
        setPopups(response.data);
        setIsVisible(true);
        // Track view for first popup
        trackView(response.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching popups:', error);
    }
  }, []);

  useEffect(() => {
    if (user) {
      // Small delay to ensure smooth page load
      const timer = setTimeout(() => {
        fetchPopups();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [user, fetchPopups]);

  const trackView = async (popupId) => {
    try {
      await axios.post(`${API}/popups/${popupId}/view`);
    } catch (error) {
      console.error('Error tracking view:', error);
    }
  };

  const trackClick = async (popupId) => {
    try {
      await axios.post(`${API}/popups/${popupId}/click`);
    } catch (error) {
      console.error('Error tracking click:', error);
    }
  };

  const dismissPopup = async () => {
    const currentPopup = popups[currentIndex];
    setIsAnimating(true);
    
    try {
      await axios.post(`${API}/popups/${currentPopup.id}/dismiss`);
    } catch (error) {
      console.error('Error dismissing popup:', error);
    }

    // Animate out
    setTimeout(() => {
      if (currentIndex < popups.length - 1) {
        const nextIndex = currentIndex + 1;
        setCurrentIndex(nextIndex);
        // Track view for next popup
        trackView(popups[nextIndex].id);
        setIsAnimating(false);
      } else {
        setIsVisible(false);
      }
    }, 200);
  };

  const handleButtonClick = (url, popupId) => {
    trackClick(popupId);
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  if (!isVisible || popups.length === 0) return null;

  const currentPopup = popups[currentIndex];
  const typeConfig = POPUP_ICONS[currentPopup.popup_type] || POPUP_ICONS.info;
  const IconComponent = typeConfig.icon;

  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      data-testid="popup-notification-overlay"
    >
      <div 
        className={`relative w-full max-w-md ${typeConfig.bg} ${typeConfig.border} border-2 rounded-xl shadow-2xl transform transition-all duration-300 ${
          isAnimating ? 'opacity-0 scale-95' : 'opacity-100 scale-100 animate-in fade-in zoom-in'
        }`}
        data-testid="popup-notification-card"
      >
        {/* Close button */}
        <button
          onClick={dismissPopup}
          className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-gray-200/50 transition-colors z-10"
          aria-label="Fermer"
          data-testid="popup-close-btn"
        >
          <X className="h-5 w-5 text-gray-500" />
        </button>

        {/* Header with icon */}
        <div className="flex items-center gap-3 p-4 pb-2">
          <div className={`p-2.5 rounded-full ${typeConfig.bg} ${typeConfig.border} border shrink-0`}>
            <IconComponent className={`h-6 w-6 ${typeConfig.color}`} />
          </div>
          <h3 className="font-bold text-lg text-gray-900 pr-8 leading-tight">
            {currentPopup.title}
          </h3>
        </div>

        {/* Image */}
        {currentPopup.image_url && (
          <div className="px-4 pb-3">
            <img 
              src={currentPopup.image_url} 
              alt={currentPopup.title}
              className="w-full h-40 object-cover rounded-lg shadow-sm"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
              data-testid="popup-image"
            />
          </div>
        )}

        {/* Content */}
        <div className="px-4 pb-4">
          <div 
            className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap"
            dangerouslySetInnerHTML={{ __html: currentPopup.content.replace(/\n/g, '<br/>') }}
            data-testid="popup-content"
          />
        </div>

        {/* Action Buttons */}
        {(currentPopup.action_button_text || currentPopup.secondary_button_text) && (
          <div className="flex gap-2 px-4 pb-4">
            {currentPopup.action_button_text && (
              <Button
                onClick={() => handleButtonClick(currentPopup.action_button_url, currentPopup.id)}
                className="flex-1 bg-gray-900 hover:bg-gray-800 text-white"
                data-testid="popup-action-btn"
              >
                {currentPopup.action_button_text}
                {currentPopup.action_button_url && (
                  <ExternalLink className="h-4 w-4 ml-2" />
                )}
              </Button>
            )}
            {currentPopup.secondary_button_text && (
              <Button
                variant="outline"
                onClick={() => handleButtonClick(currentPopup.secondary_button_url, currentPopup.id)}
                className="flex-1"
                data-testid="popup-secondary-btn"
              >
                {currentPopup.secondary_button_text}
              </Button>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-3 bg-white/50 rounded-b-xl border-t border-gray-200/50">
          <span className="text-xs text-gray-500">
            {popups.length > 1 && (
              <span className="flex items-center gap-1">
                <span className="font-medium">{currentIndex + 1}</span>
                <span>/</span>
                <span>{popups.length}</span>
                <span className="ml-1">messages</span>
              </span>
            )}
          </span>
          <Button
            onClick={dismissPopup}
            size="sm"
            className="bg-gray-900 hover:bg-gray-800 text-white px-4"
            data-testid="popup-dismiss-btn"
          >
            {currentIndex < popups.length - 1 ? 'Suivant' : 'Compris'}
          </Button>
        </div>

        {/* Progress indicator for multiple popups */}
        {popups.length > 1 && (
          <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 flex gap-1.5">
            {popups.map((_, index) => (
              <div
                key={index}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index === currentIndex 
                    ? 'bg-white w-4' 
                    : index < currentIndex 
                      ? 'bg-white/60' 
                      : 'bg-white/30'
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PopupNotification;
